import { useState } from "react";
import { Form, Modal, ModalProps } from "antd";
import { updateRecord } from "../../utils/slice";
import { validateFields } from "../../../../../config/Global";
import SVGIcon from "../../../../../utils/SVGIcon";
import Config from "../../../../../config/Config";
import CancelButton from "../../../../../components/Buttons/CancelButton";
import SaveButton from "../../../../../components/Buttons/SaveButton";
import FormComponent from "./FormComponent";
import { assignErrorToInput } from "../../../../../store/api";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../utils/hooks/hooks";

interface EditDrawerProps extends ModalProps {
  titleName: string;
  open: boolean;
  close: () => void;
}

/**
 * Renders an edit component for a Ethnicity Management record.
 *
 * @param {string} titleName - The name of the title for the modal.
 * @param {boolean} open - Whether the modal is open or not.
 * @param {function} close - Function to close the modal.
 * @param {...any} rest - Additional props to be spread onto the Modal component.
 * @return {JSX.Element} The rendered edit component for a Ethnicity Management record.
 */
const EditComponent: React.FC<EditDrawerProps> = ({
  titleName,
  open,
  close,
  ...rest
}) => {
  const dispatch = useAppDispatch();
  const details = useAppSelector(state => state.race.details);
  const [form] = Form.useForm();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const drawerClose = () => {
    close();
    form.resetFields();
    setDisabled(true)
  };

 /**
  * Validates the form data and updates the form fields accordingly.
  *
  * @param {any} data - The data to be validated and updated.
  * @return {void} This function does not return anything.
  */
  const validateForm = () => {
    validateFields(form, setDisabled);
  };

  /**
   * Submits the form data to the server and handles the response.
   *
   * @param {any} data - The form data to be submitted.
   * @return {void} This function does not return anything.
   */
  const handleSubmit = (data: any) => {
    setSaving(true);
    dispatch(updateRecord(details.id, data))
      .then(() => {
        drawerClose();
      })
      .catch((error: any) => {
        assignErrorToInput(form, error?.STATUS);
      })
      .finally(() => setSaving(false));
  };

  return (
    <Modal
      title={`Edit ${titleName}`}
      open={open}
      width={"35%"}
      onCancel={drawerClose}
      destroyOnClose
      closeIcon={<SVGIcon icon="close" color={Config.themePrimaryBtn} />}
      footer={[
        <CancelButton key={2} onClick={drawerClose} />,
        <SaveButton
          key={1}
          form="editForm"
          disabled={disabled}
          loading={saving}
        />,
      ]}
      {...rest}
    >
      <FormComponent
        form={form}
        id="editForm"
        editValues={details}
        handleSubmit={handleSubmit}
        onValuesChange={validateForm}
      />
    </Modal>
  );
};

export default EditComponent;
